(() => {
  const makeLabels = {
    citroen: "Citroën",
    fiat: "Fiat",
    ford: "Ford",
    freightliner: "Freightliner",
    mb: "Mercedes-Benz",
    peugeot: "Peugeot",
    ram: "Ram",
    vw: "Volkswagen",
  };

  const modelLabels = {
    "boxer-x250-x290": "Boxer — X250 / X290",
    "crafter-2e-2f": "Crafter — 2E / 2F",
    "ducato-x250-x290": "Ducato — X250 / X290",
    "id-buzz-meb": "ID. Buzz — MEB",
    "jumper-relay-x250-x290": "Jumper / Relay — X250 / X290",
    "metris-vito-v-class-w447": "Metris / Vito / V-Class — W447",
    "promaster-x250-x290": "ProMaster — X250 / X290",
    "sprinter-906-ncv3": "Sprinter — 906 / NCV3",
    "sprinter-907-vs30": "Sprinter — 907 / VS30",
    "transit-v363": "Transit 150 / 250 / 350 — V363",
    "vanagon-transporter-t3-t25": "Vanagon / Transporter — T3 / T25",
  };

  const modelCodes = Object.keys(modelLabels).sort((a, b) => b.length - a.length);
  const catalogCache = new Map();
  const legacyFitmentParams = [
    "filter.p.m.custom.make",
    "filter.p.m.custom.model",
    "filter.p.m.custom.year",
    "tw_make",
    "tw_model",
    "tw_year",
  ];

  function addPreviewParams(url) {
    const currentUrl = new URL(window.location.href);
    ["preview_theme_id", "pb", "_fd"].forEach((parameter) => {
      if (currentUrl.searchParams.has(parameter)) {
        url.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
      }
    });

    const currentTheme = window.terrawagenThemePreview || window.Shopify?.theme;
    if (
      !url.searchParams.has("preview_theme_id") &&
      currentTheme?.id &&
      currentTheme?.role !== "main"
    ) {
      url.searchParams.set("preview_theme_id", currentTheme.id);
      url.searchParams.set("_fd", "0");
      url.searchParams.set("pb", "0");
    }

    return url;
  }

  function parseExactTag(tag) {
    if (!tag?.startsWith("twfit-")) return null;

    for (const make of Object.keys(makeLabels)) {
      const makePrefix = `twfit-${make}-`;
      if (!tag.startsWith(makePrefix)) continue;
      const remainder = tag.slice(makePrefix.length);

      for (const model of modelCodes) {
        const modelPrefix = `${model}-`;
        if (!remainder.startsWith(modelPrefix)) continue;
        const year = remainder.slice(modelPrefix.length);
        if (year === "all" || /^\d{4}$/.test(year)) {
          return { make, model, year, tag };
        }
      }
    }

    return null;
  }

  function normalizeCatalog(products) {
    return (Array.isArray(products) ? products : [])
      .map((product) => ({
        handle: product.handle,
        combos: [...new Set(product.tags || [])]
          .map(parseExactTag)
          .filter(Boolean),
      }))
      .filter((product) => product.handle && product.combos.length > 0);
  }

  function buildCatalogUrl(sourceUrl, includeNativeFilters = false) {
    const url = new URL(sourceUrl, window.requestShopUrl || window.location.origin);
    url.searchParams.set("view", "fitment_data");

    if (includeNativeFilters) {
      const current = new URL(window.location.href);
      current.searchParams.forEach((value, parameter) => {
        if (
          parameter.startsWith("filter.") &&
          !legacyFitmentParams.includes(parameter)
        ) {
          url.searchParams.append(parameter, value);
        }
      });
    }

    return addPreviewParams(url).toString();
  }

  async function loadCatalog(sourceUrl, includeNativeFilters = false) {
    const url = buildCatalogUrl(sourceUrl, includeNativeFilters);
    if (!catalogCache.has(url)) {
      catalogCache.set(
        url,
        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`Fitment catalog request failed: ${response.status}`);
            return response.json();
          })
          .then(normalizeCatalog)
          .catch((error) => {
            catalogCache.delete(url);
            throw error;
          }),
      );
    }
    return catalogCache.get(url);
  }

  function comboMatches(combo, selection, ignoredDimension = null) {
    return ["make", "model", "year"].every(
      (dimension) =>
        dimension === ignoredDimension ||
        !selection[dimension] ||
        combo[dimension] === selection[dimension],
    );
  }

  function optionCounts(catalog, selection, dimension) {
    const values = new Map();
    catalog.forEach((product) => {
      const productValues = new Set(
        product.combos
          .filter((combo) => comboMatches(combo, selection, dimension))
          .map((combo) => combo[dimension]),
      );
      productValues.forEach((value) => values.set(value, (values.get(value) || 0) + 1));
    });
    return values;
  }

  function makeLabel(code) {
    return makeLabels[code] || code;
  }

  function modelLabel(code) {
    return modelLabels[code] || code;
  }

  function yearLabel(code) {
    return code === "all" ? "Any year — confirm fitment" : code;
  }

  function exactTag(selection) {
    if (!selection.make || !selection.model || !selection.year) return "";
    return `twfit-${selection.make}-${selection.model}-${selection.year}`;
  }

  function currentSelection(tags) {
    const tagList = Array.isArray(tags) ? tags : [];
    const exact = tagList.map(parseExactTag).find(Boolean);
    if (exact) return exact;

    return {
      make: tagList.find((tag) => tag.startsWith("twmake-"))?.slice(7) || "",
      model: tagList.find((tag) => tag.startsWith("twmodel-"))?.slice(8) || "",
      year: tagList.find((tag) => tag.startsWith("twyear-"))?.slice(7) || "",
    };
  }

  function cleanLegacyUrl() {
    const url = new URL(window.location.href);
    let changed = false;
    legacyFitmentParams.forEach((parameter) => {
      if (url.searchParams.has(parameter)) {
        url.searchParams.delete(parameter);
        changed = true;
      }
    });
    if (changed) {
      url.searchParams.delete("page");
      window.location.replace(url.toString());
    }
    return changed;
  }

  window.TerrawagenFitment = {
    addPreviewParams,
    cleanLegacyUrl,
    currentSelection,
    exactTag,
    loadCatalog,
    makeLabel,
    modelLabel,
    normalizeCatalog,
    optionCounts,
    yearLabel,
  };

  if (!customElements.get("fitment-tag-filters")) {
    class FitmentTagFilters extends HTMLElement {
      constructor() {
        super();
        this.makeSelect = this.querySelector('[data-fitment-dimension="make"]');
        this.modelSelect = this.querySelector('[data-fitment-dimension="model"]');
        this.yearSelect = this.querySelector('[data-fitment-dimension="year"]');
        this.resetLink = this.querySelector("[data-fitment-reset]");
        this.status = this.querySelector("[data-fitment-status]");
        this.catalog = [];
        this.selection = currentSelection(this.readCurrentTags());
      }

      connectedCallback() {
        if (cleanLegacyUrl()) return;

        this.makeSelect?.addEventListener("change", () => this.onChange("make"));
        this.modelSelect?.addEventListener("change", () => this.onChange("model"));
        this.yearSelect?.addEventListener("change", () => this.onChange("year"));
        this.resetLink?.addEventListener("click", (event) => {
          event.preventDefault();
          this.navigate({ make: "", model: "", year: "" });
        });
        this.load();
      }

      readCurrentTags() {
        try {
          return JSON.parse(this.dataset.currentTags || "[]");
        } catch (_error) {
          return [];
        }
      }

      async load() {
        try {
          this.catalog = await loadCatalog(this.dataset.catalogUrl);
          if (this.catalog.length === 0) return;
          this.render();
          this.hidden = false;
        } catch (error) {
          console.error("Unable to load collection fitment filters.", error);
          if (this.status) this.status.textContent = "Vehicle filters are temporarily unavailable.";
          this.hidden = false;
        }
      }

      addOptions(select, counts, selected, labeler) {
        if (!select) return;
        select.querySelectorAll("option:not([data-default])").forEach((option) => option.remove());

        if (selected && !counts.has(selected)) counts.set(selected, 0);
        const values = [...counts.entries()].sort(([a], [b]) => {
          if (select === this.yearSelect) {
            if (a === "all") return 1;
            if (b === "all") return -1;
            return Number(a) - Number(b);
          }
          return labeler(a).localeCompare(labeler(b), undefined, { numeric: true });
        });

        values.forEach(([value, count]) => {
          const option = document.createElement("option");
          option.value = value;
          option.textContent = `${labeler(value)} (${count})`;
          option.selected = value === selected;
          select.append(option);
        });
      }

      render() {
        const makeCounts = optionCounts(this.catalog, {}, "make");
        this.addOptions(this.makeSelect, makeCounts, this.selection.make, makeLabel);

        const modelCounts = this.selection.make
          ? optionCounts(this.catalog, { make: this.selection.make }, "model")
          : new Map();
        this.addOptions(this.modelSelect, modelCounts, this.selection.model, modelLabel);
        this.modelSelect.disabled = !this.selection.make;

        const yearCounts = this.selection.make && this.selection.model
          ? optionCounts(
              this.catalog,
              { make: this.selection.make, model: this.selection.model },
              "year",
            )
          : new Map();
        this.addOptions(this.yearSelect, yearCounts, this.selection.year, yearLabel);
        this.yearSelect.disabled = !this.selection.make || !this.selection.model;

        if (this.status) {
          if (this.selection.year) {
            this.status.textContent = "Showing exact matches for this vehicle and year.";
          } else if (this.selection.model) {
            this.status.textContent = "Choose a model year to show matching products.";
          } else if (this.selection.make) {
            this.status.textContent = "Choose a model / chassis, then model year.";
          } else {
            this.status.textContent = "Choose make, model / chassis, then year.";
          }
        }
        this.resetLink.hidden = !this.selection.make && !this.selection.model && !this.selection.year;
      }

      onChange(dimension) {
        this.selection[dimension] = this[`${dimension}Select`].value;
        if (dimension === "make") {
          this.selection.model = "";
          this.selection.year = "";
        } else if (dimension === "model") {
          this.selection.year = "";
        }

        if (dimension === "make" && !this.selection.make) {
          this.navigate({ make: "", model: "", year: "" });
          return;
        }

        // Make and model options are already present in the cached storewide
        // catalog. Update those dependent fields in place and wait until a
        // complete vehicle is chosen before loading its collection results.
        if (dimension !== "year" || !this.selection.year) {
          this.render();
          return;
        }

        this.navigate(this.selection);
      }

      navigate(selection) {
        const tags = [];
        if (selection.year && selection.make && selection.model) {
          tags.push(exactTag(selection));
        } else {
          if (selection.make) tags.push(`twmake-${selection.make}`);
          if (selection.model) tags.push(`twmodel-${selection.model}`);
        }

        const base = new URL(
          this.dataset.catalogUrl || "/collections/all",
          window.requestShopUrl || window.location.origin,
        );
        base.search = "";
        base.pathname = `${base.pathname.replace(/\/$/, "")}${
          tags.length ? `/${tags.join("+")}` : ""
        }`;
        const current = new URL(window.location.href);
        current.searchParams.forEach((value, parameter) => {
          if (
            !legacyFitmentParams.includes(parameter) &&
            parameter !== "page" &&
            parameter !== "view"
          ) {
            base.searchParams.append(parameter, value);
          }
        });
        window.location.assign(addPreviewParams(base).toString());
      }
    }

    customElements.define("fitment-tag-filters", FitmentTagFilters);
  }
})();
